import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { brl, fmtDate } from "../lib/format";
import { Plus, Trash, VideoCamera } from "@phosphor-icons/react";
import { toast } from "sonner";
import PeriodFilter, { toQuery } from "../components/PeriodFilter";
import { useAuth } from "../context/AuthContext";

const EMPTY_LIVE = {
  name: "", date: new Date().toISOString().slice(0, 10), note: "", contractor_id: "",
  pix_key: "", pix_type: "", pix_name: "", commission_pct: "",
};
const PIX_TYPES = [
  { v: "", label: "—" },
  { v: "cpf", label: "CPF" },
  { v: "cnpj", label: "CNPJ" },
  { v: "email", label: "E-mail" },
  { v: "telefone", label: "Telefone" },
  { v: "aleatoria", label: "Aleatória" },
];

export default function Lives() {
  const [lives, setLives] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(EMPTY_LIVE);
  const [creating, setCreating] = useState(false);
  const [period, setPeriod] = useState({ from: "", to: "" });
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const canWrite = hasPerm("lives.write");
  const canDelete = hasPerm("lives.delete");

  const load = useCallback(async () => {
    const qs = toQuery(period);
    const results = await Promise.allSettled([
      api.get(`/lives${qs}`),
      api.get("/contractors"),
      api.get("/settings"),
    ]);
    setLives(results[0].status === "fulfilled" ? results[0].value.data : []);
    setContractors(results[1].status === "fulfilled" ? results[1].value.data : []);
    setSettings(results[2].status === "fulfilled" ? results[2].value.data : null);
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Dê um nome para a live");
    setCreating(true);
    try {
      const payload = {
        ...form,
        contractor_id: form.contractor_id || null,
        pix_key: form.pix_key || null,
        pix_type: form.pix_type || null,
        pix_name: form.pix_name || null,
        commission_pct: form.commission_pct === "" || form.commission_pct === null
          ? null
          : parseFloat(String(form.commission_pct).replace(",", ".")),
      };
      const { data } = await api.post("/lives", payload);
      toast.success("Live criada!");
      setForm(EMPTY_LIVE);
      await load();
      nav(`/lives/${data.id}`);
    } catch { toast.error("Erro ao criar live"); }
    setCreating(false);
  };

  const fillStorePix = () => {
    if (!settings?.pix_key) {
      toast.error("Cadastre primeiro a chave Pix da loja em Contratantes");
      return;
    }
    setForm(f => ({
      ...f,
      pix_key: settings.pix_key || "",
      pix_type: settings.pix_type || "",
      pix_name: settings.pix_name || "",
    }));
    toast.success("Pix da loja aplicado");
  };

  const remove = async (id) => {
    if (!window.confirm("Apagar esta live e todos os pedidos dela?")) return;
    await api.delete(`/lives/${id}`);
    toast.success("Live removida");
    load();
  };

  return (
    <div data-testid="lives-page" className="space-y-8">
      <header>
        <p className="label-brutal">Eventos</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Suas Lives</h1>
        <p className="text-brand-ink2 mt-2">Organize cada live com nome e data. Todos os pedidos ficam agrupados por evento.</p>
      </header>

      <PeriodFilter value={period} onChange={setPeriod} testid="lives-period" />

      {canWrite && (
      <form onSubmit={submit} className="card-brutal p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.2fr_140px_1fr_140px_1fr] gap-4">
          <div>
            <label className="label-brutal">Nome da live</label>
            <input
              data-testid="live-name-input"
              className="input-brutal"
              placeholder="Ex: Live Coleção Verão"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="label-brutal">Data</label>
            <input
              data-testid="live-date-input"
              type="date"
              className="input-brutal"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
          <div>
            <label className="label-brutal">Contratante</label>
            <select
              data-testid="live-contractor-select"
              className="input-brutal"
              value={form.contractor_id}
              onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}
            >
              <option value="">— Nossa loja —</option>
              {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-brutal" title="% do valor recebido que fica com o Desapegue">% Desapegue</label>
            <input
              data-testid="live-commission-pct-input"
              type="number"
              step="0.01"
              min="0"
              max="100"
              className="input-brutal"
              placeholder="Ex: 30"
              value={form.commission_pct}
              onChange={(e) => setForm({ ...form, commission_pct: e.target.value })}
            />
          </div>
          <div>
            <label className="label-brutal">Observação</label>
            <input
              data-testid="live-note-input"
              className="input-brutal"
              placeholder="Opcional"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </div>
        </div>

        <div className="border-t-2 border-dashed border-brand-ink pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="label-brutal !mb-0">Chave Pix para cobrança</p>
              <p className="text-xs text-brand-ink2">Aparece automaticamente na mensagem. Deixe vazio se não quiser incluir Pix.</p>
            </div>
            {settings?.pix_key && (
              <button data-testid="live-use-store-pix" type="button" onClick={fillStorePix} className="btn-white !px-3 !py-2">
                Usar Pix da loja
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_1fr_auto] gap-4 items-end">
            <div>
              <label className="label-brutal">Tipo</label>
              <select
                data-testid="live-pix-type"
                className="input-brutal"
                value={form.pix_type}
                onChange={(e) => setForm({ ...form, pix_type: e.target.value })}
              >
                {PIX_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-brutal">Chave Pix</label>
              <input
                data-testid="live-pix-key"
                className="input-brutal"
                placeholder="Ex: 28885915000140"
                value={form.pix_key}
                onChange={(e) => setForm({ ...form, pix_key: e.target.value })}
              />
            </div>
            <div>
              <label className="label-brutal">Titular</label>
              <input
                data-testid="live-pix-name"
                className="input-brutal"
                placeholder="Ex: Herida Caroline"
                value={form.pix_name}
                onChange={(e) => setForm({ ...form, pix_name: e.target.value })}
              />
            </div>
            <button data-testid="create-live-btn" type="submit" disabled={creating} className="btn-pink h-[52px]">
              <Plus weight="bold" size={18} /> Criar
            </button>
          </div>
        </div>
      </form>
      )}

      {lives.length === 0 ? (
        <div className="card-brutal p-10 text-center">
          <VideoCamera weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Nada por aqui ainda</p>
          <p className="text-brand-ink2 text-sm">Crie sua primeira live para começar a registrar pedidos.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lives.map((lv) => (
            <div key={lv.id} data-testid={`live-card-${lv.id}`} className="card-brutal card-brutal-hover p-5 flex flex-col">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-brand-ink2">{fmtDate(lv.date)}</p>
                  <p className="font-heading text-xl font-black mt-1">{lv.name}</p>
                  {lv.contractor_id && (() => {
                    const ctr = contractors.find(c => c.id === lv.contractor_id);
                    return ctr ? <p className="text-xs mt-1"><span className="badge-brutal bg-brand-yellow">Contratante: {ctr.name}</span></p> : null;
                  })()}
                  {lv.commission_pct != null && (
                    <p className="text-xs mt-1"><span className="badge-brutal bg-brand-mint">Desapegue: {lv.commission_pct}%</span></p>
                  )}
                  {lv.note && <p className="text-sm text-brand-ink2 mt-1">{lv.note}</p>}
                </div>
                <button
                  data-testid={`delete-live-${lv.id}`}
                  onClick={() => remove(lv.id)}
                  title="Excluir"
                  className={`p-2 border-2 border-brand-ink bg-white hover:bg-brand-peach transition-colors ${canDelete ? "" : "hidden"}`}
                >
                  <Trash weight="bold" size={16} />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="bg-brand-yellow border-2 border-brand-ink p-2 text-center">
                  <p className="text-[10px] font-bold uppercase">Total</p>
                  <p className="font-heading font-black text-sm">{brl(lv.total)}</p>
                </div>
                <div className="bg-brand-mint border-2 border-brand-ink p-2 text-center">
                  <p className="text-[10px] font-bold uppercase">Pago</p>
                  <p className="font-heading font-black text-sm">{brl(lv.paid)}</p>
                </div>
                <div className="bg-brand-peach border-2 border-brand-ink p-2 text-center">
                  <p className="text-[10px] font-bold uppercase">A receber</p>
                  <p className="font-heading font-black text-sm">{brl(lv.pending)}</p>
                </div>
              </div>

              <div className="mt-4 flex gap-2 flex-wrap">
                <span className="badge-brutal bg-white">{lv.orders_count} pedidos</span>
                <span className="badge-brutal bg-white">{lv.customers_count} clientes</span>
              </div>

              <button
                data-testid={`open-live-${lv.id}`}
                onClick={() => nav(`/lives/${lv.id}`)}
                className="btn-pink mt-4 w-full"
              >
                Abrir live
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
